import { AppUpdateNoticeComponent } from './app-update/app-update-notice.component';
import { MaintenanceNoticeComponent } from './maintenance/maintenance-notice.component';
import { MaintenanceService } from './maintenance/maintenance.service';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { HttpClient } from '@angular/common/http';
import { Component, DestroyRef, LOCALE_ID, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DateAdapter } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { Event, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import moment from 'moment';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ConfigService } from './services/config.service';
import { CookieService } from './services/cookie.service';
import { GoogleMapsService } from './services/google-maps.service';
import { StorageService } from './services/storage.service';
import { ViewportService } from './services/viewport.service';
import { setLangCookies } from './utils/profile.utils';
import { NotificationService } from './services/notification.service';

@Component({
  selector: 'hb-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [RouterOutlet, MaintenanceNoticeComponent, AppUpdateNoticeComponent]
})
export class AppComponent implements OnInit {
  readonly maintenance = inject(MaintenanceService);
  dialog = inject(MatDialog);
  private breakpointObserver = inject(BreakpointObserver);
  private matIconRegistry = inject(MatIconRegistry);
  private domSanitizer = inject(DomSanitizer);
  locale = inject(LOCALE_ID);
  private _adapter = inject<DateAdapter<Date>>(DateAdapter);
  private configService = inject(ConfigService);
  private router = inject(Router);
  private readonly toast = inject(NotificationService);
  private cookieService = inject(CookieService);
  private http = inject(HttpClient);
  private googleMapsService = inject(GoogleMapsService);
  private storageService = inject(StorageService);
  private destroyRef = inject(DestroyRef);
  private viewportService = inject(ViewportService);

  private _appState: 'loading' | 'ready' = 'loading';

  public get appState(): 'loading' | 'ready' {
    return this._appState;
  }

  public set appState(value: 'loading' | 'ready') {
    this._appState = value;
    if (value === 'ready') {
      document.getElementById('hb-splash')?.remove();
    }
  }
  private localLang!: string;
  private currentScreenSize = 'Unknown';
  private citySelectionInProgress = false;
  // Create a map to display breakpoint names for demonstration purposes.
  private displayNameMap = new Map([
    [Breakpoints.XSmall, 'XSmall'],
    [Breakpoints.Small, 'Small'],
    [Breakpoints.Medium, 'Medium'],
    [Breakpoints.Large, 'Large'],
    [Breakpoints.XLarge, 'XLarge']
  ]);

  constructor() {
    const locale = this.locale;

    // Get Local Language
    this.localLang = locale?.split('-')[0].toString();

    this._adapter.setLocale(locale);
    moment.locale(locale);

    this.breakpointObserver
      .observe([Breakpoints.XSmall, Breakpoints.Small, Breakpoints.Medium, Breakpoints.Large, Breakpoints.XLarge])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        for (const query of Object.keys(result.breakpoints)) {
          if (result.breakpoints[query]) {
            this.currentScreenSize = this.displayNameMap.get(query) ?? 'Unknown';
          }
        }
      });
  }

  public ngOnInit(): void {
    // Defer icon registration until after first paint — not needed for initial render
    setTimeout(() => this.registerIcons(), 0);

    // Track virtual keyboard height in CSS var --keyboard-inset-bottom
    // (used by modals and sticky footers to stay above the keyboard).
    this.viewportService.init();

    this.router.events.subscribe((event: Event) => {
      if (event instanceof NavigationEnd) {
        const currentRoute = event.url;

        // Remove splash immediately — auth and geo dialog must never block the UI
        this.appState = 'ready';

        if (!currentRoute.includes('network-error') && !currentRoute.includes('404')) {
          this.ensureCityAndLanguage();
        }
      }
    });
    if (!this.storageService.getValue('store-showedWarning')) {
      this.storageService.setValue('store-showedWarning', 'true');
      this.toast.show(
        $localize`:@@respectfulLanguageWarning:Let\'s keep our space respectful - avoid using offensive language, please`,
        $localize`:@@close:Close`,
        5000
      );
    }
  }

  private async openSetGeoDialog(): Promise<void> {
    this.citySelectionInProgress = true;
    const { GeoSettingComponent } = await import('./modals/geo-setting/geo-setting.component');
    const dialogRef = this.dialog.open(GeoSettingComponent, {
      height: '100%',
      width: '100%',
      maxWidth: '600px',
      panelClass: 'hb-dialog-container',
      autoFocus: 'dialog'
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(async (lang) => {
        this.citySelectionInProgress = false;
        this.configService.notifyCityChange(this.storageService.getValue('store-cityName'));

        if (lang) {
          let country = 'GB';
          if (lang === 'uk') {
            country = 'UA';
          }

          setLangCookies(this.cookieService, country, lang);

          // Navigate to the same route under the new locale prefix
          const currentPath = this.router.url; // e.g. '/home' — app-relative, no baseHref
          if (navigator.serviceWorker) {
            const registrations = await navigator.serviceWorker.getRegistrations();
            await Promise.all(registrations.map((r) => r.unregister()));
          }
          window.location.href = `/${lang}${currentPath}`;
        }
        // appState is already 'ready' — splash was removed on NavigationEnd
      });
  }

  /** Show geo dialog if city or language is missing. Tries IP auto-detect for city first. */
  private ensureCityAndLanguage(): void {
    const hasCity = !!this.storageService.getValue('store-cityId');
    const hasLanguage = !!this.storageService.getValue('store-language');

    if ((hasCity && hasLanguage) || this.citySelectionInProgress) {
      return;
    }

    // If the URL contains a ?city= param, seed city+language from it immediately
    // (same logic as EventsComponent.ngOnInit) so we don't open the geo dialog.
    // TODO(post-mvp): resolve Google Place ID → internal city UUID dynamically for multi-city.
    if (!hasCity) {
      const urlParams = new URLSearchParams(this.router.url.split('?')[1] ?? '');
      if (urlParams.has('city')) {
        this.storageService.setValue('store-cityId', environment.mvpCityId);
        this.storageService.setValue('store-cityName', $localize`:@@cityLviv:Lviv`);
        if (!hasLanguage) {
          this.storageService.setValue('store-language', this.locale.split('-')[0]);
        }
        this.configService.notifyCityChange(this.storageService.getValue('store-cityName'));
        return;
      }
    }

    if (!hasCity) {
      this.citySelectionInProgress = true;
      this.getCityFromIp()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((cityName) => {
          if (!cityName) {
            void this.openSetGeoDialog();
            return;
          }

          this.googleMapsService.getPlaceIdByCityName(cityName).subscribe((response) => {
            const placeId = response?.results?.[0]?.place_id;
            if (!placeId) {
              void this.openSetGeoDialog();
              return;
            }

            this.storageService.setValue('store-cityName', cityName);
            this.storageService.setValue('store-cityId', placeId);
            this.configService.notifyCityChange(cityName);
            if (!hasLanguage) {
              void this.openSetGeoDialog();
            } else {
              this.citySelectionInProgress = false;
            }
          });
        });
    } else {
      // Has city but no language
      void this.openSetGeoDialog();
    }
  }

  private getCityFromIp(): Observable<string | null> {
    if (!environment.countryByIpApiKey) {
      return of(null);
    }
    return this.http.get<any>(`https://api.ipregistry.co/?key=${environment.countryByIpApiKey}`).pipe(
      map((response) => response?.location?.city ?? null),
      catchError(() => of(null))
    );
  }

  // ToDo: move to Service shared icons or to proper components if icons are not shared
  private registerIcons(): void {
    this.matIconRegistry.addSvgIcon(
      'account_box',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/account_box.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'globe',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/globe.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'dashboard',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/dashboard.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'setup',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/setup.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'location',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/location.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'call',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/call.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'facebook',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/facebook.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'home',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/home.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'email',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/email.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'phone',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/phone.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'instagram',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/instagram.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'location-blue',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/location-blue.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'tree',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/tree.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'football',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/football.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'twitter',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/twitter.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'bookmark',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/bookmark_border.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'bookmark-add',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/bookmark_add.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'send',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/send.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'edit',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/edit.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'delete',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/delete.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'notification',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/notification.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'chevron_down',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/chevron_down.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'chevron_up',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/chevron_up.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'search',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/search.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'done',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/done.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'logout',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/logout.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'reddit',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/reddit.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'telegram',
      this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/telegram.svg')
    );
  }
}
